import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import catchError from '../../components/global/CatchError'
import { useLangSelected } from '../../hooks/useLangSelected'
import { toast } from 'react-toastify'
import { SERVER_URL, apiHeader } from '../../config'
import { Skill } from '../../types/skill.type'
import ExerciseForm from './ExerciseForm'

type CoachExerciseFormModalProps = {
    show: boolean
    onClose: () => void
    exercise?: Exercise
    onSuccess: () => void
    skills: Skill[]
}

type Exercise = {
    id?: number
    title: string
    description: string
    peopleCount: number
    intensity: 'LOW' | 'MEDIUM' | 'HIGH'
    skillIds: number[]
}

export default function CoachExerciseFormModal({
    show,
    onClose,
    exercise,
    onSuccess,
    skills,
}: Readonly<CoachExerciseFormModalProps>) {
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const navigate = useNavigate()
    const lng = useLangSelected()

    const { mutate: createExercise } = useMutation({
        mutationFn: async (data: Omit<Exercise, 'id'>) => {
            await axios.post(`${SERVER_URL}/exercises`, data, apiHeader())
            toast.success(t('coach.exercises.createSuccess'))
        },
        onSuccess: onSuccess,
        onError: (error: any) => {
            if (error.response?.status === 401) {
                catchError({ status: 401, message: 'Votre session a expiré' }, () => {
                    localStorage.removeItem('tokenCoach')
                    navigate(`/${lng}/login`)
                })
            } else {
                const errorMessage =
                    error.response?.data?.message ?? t('coach.exercises.createError')
                toast.error(errorMessage)
            }
        },
        onSettled: () => setIsSubmitting(false),
    })

    const { mutate: updateExercise } = useMutation({
        mutationFn: async (data: Exercise) => {
            await axios.patch(`${SERVER_URL}/exercises/${data.id}`, data, apiHeader())
            toast.success(t('coach.exercises.updateSuccess'))
        },
        onSuccess: onSuccess,
        onError: (error: any) => {
            if (error.response?.status === 401) {
                catchError({ status: 401, message: 'Votre session a expiré' }, () => {
                    localStorage.removeItem('tokenCoach')
                    navigate(`/${lng}/login`)
                })
            } else {
                const errorMessage =
                    error.response?.data?.message ?? t('coach.exercises.updateError')
                toast.error(errorMessage)
            }
        },
        onSettled: () => setIsSubmitting(false),
    })

    const handleSubmit = (data: any) => {
        setIsSubmitting(true)
        if (exercise?.id) {
            updateExercise({ ...data, id: exercise.id })
        } else {
            createExercise(data)
        }
    }

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{exercise?.id ? t('common.edit') : t('common.create')} {t('coach.exercises.exercise')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ExerciseForm
                    initial={exercise}
                    skills={skills}
                    loading={isSubmitting}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                />
            </Modal.Body>
        </Modal>
    )
}
